import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Plus, X, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { TaskProject } from '../../types';
import { PROJECT_COLORS } from '../../utils/projectConstants';
import { hexToRgba, sortTaskProjects } from '../../utils/projectUtils';

interface ProjectSelectorProps {
  projects: TaskProject[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (project: TaskProject, newName: string) => Promise<void>;
  onDelete: (project: TaskProject) => void;
  onEdit?: (project: TaskProject) => void;
  onReorder?: (orderedIds: string[]) => Promise<void>;
  /** Color de fondo de la página (para degradés del scroll) */
  fadeBackground?: string;
}

const SCROLL_FADE_WIDTH = 44;
const HOVER_ACTIONS_DELAY_MS = 3000;

function buildScrollMask(left: boolean, right: boolean): string | undefined {
  const f = `${SCROLL_FADE_WIDTH}px`;
  if (left && right) {
    return `linear-gradient(to right, transparent, black ${f}, black calc(100% - ${f}), transparent)`;
  }
  if (left) return `linear-gradient(to right, transparent, black ${f})`;
  if (right) return `linear-gradient(to right, black calc(100% - ${f}), transparent)`;
  return undefined;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onEdit,
  onReorder,
  fadeBackground,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasDraggingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hoverActionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [actionsVisibleId, setActionsVisibleId] = useState<string | null>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const activeProjects = useMemo(
    () => sortTaskProjects(projects.filter(p => p.status !== 'archived')),
    [projects]
  );

  const handleDragStart = useCallback(() => {
    wasDraggingRef.current = true;
  }, []);

  const handleDragEnd = useCallback(async (result: DropResult) => {
    setTimeout(() => { wasDraggingRef.current = false; }, 100);

    if (!onReorder || !result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(activeProjects);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    await onReorder(reordered.map(project => project.id));
  }, [activeProjects, onReorder]);

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 4;
    const overflow = el.scrollWidth > el.clientWidth + threshold;
    setHasOverflow(overflow);
    setShowLeftFade(overflow && el.scrollLeft > threshold);
    setShowRightFade(overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - threshold);
  }, []);

  useEffect(() => {
    updateScrollFades();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollFades, { passive: true });
    const ro = new ResizeObserver(updateScrollFades);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollFades);
      ro.disconnect();
    };
  }, [activeProjects.length, updateScrollFades]);

  useEffect(() => () => {
    if (hoverActionsTimerRef.current) clearTimeout(hoverActionsTimerRef.current);
  }, []);

  const handlePillMouseEnter = useCallback((projectId: string) => {
    if (hoverActionsTimerRef.current) clearTimeout(hoverActionsTimerRef.current);
    setActionsVisibleId(null);
    hoverActionsTimerRef.current = setTimeout(() => {
      setActionsVisibleId(projectId);
    }, HOVER_ACTIONS_DELAY_MS);
  }, []);

  const handlePillMouseLeave = useCallback(() => {
    if (hoverActionsTimerRef.current) clearTimeout(hoverActionsTimerRef.current);
    hoverActionsTimerRef.current = null;
    setActionsVisibleId(null);
  }, []);

  const scrollMask = useMemo(
    () => buildScrollMask(showLeftFade, showRightFade),
    [showLeftFade, showRightFade]
  );

  const scroll = (dir: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
    setTimeout(updateScrollFades, 300);
  };

  const startEditing = useCallback((project: TaskProject, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (hoverActionsTimerRef.current) clearTimeout(hoverActionsTimerRef.current);
    hoverActionsTimerRef.current = null;
    setActionsVisibleId(null);
    setEditingId(project.id);
    setEditName(project.name);
    setTimeout(() => inputRef.current?.select(), 0);
  }, []);

  const commitRename = useCallback(async (project: TaskProject) => {
    const trimmed = editName.trim();
    setEditingId(null);
    if (!trimmed || trimmed === project.name) return;
    await onRename(project, trimmed);
  }, [editName, onRename]);

  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditName('');
  }, []);

  const fadeFrom = fadeBackground ?? 'rgb(249 250 251 / 0.95)';

  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0 max-w-2xl">
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="relative flex-1 min-w-0">
        <Droppable droppableId="project-pills" direction="horizontal">
          {provided => (
        <div
          ref={el => {
            scrollRef.current = el;
            provided.innerRef(el);
          }}
          {...provided.droppableProps}
          className={`flex items-center gap-2 overflow-x-auto py-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${
            hasOverflow ? 'px-7' : 'px-0.5'
          }`}
          style={
            scrollMask
              ? {
                  maskImage: scrollMask,
                  WebkitMaskImage: scrollMask,
                }
              : undefined
          }
        >
          {activeProjects.length === 0 && (
            <span className="text-xs text-gray-400 px-2 whitespace-nowrap">Sin proyectos</span>
          )}
          {activeProjects.map((project, index) => {
            const isSelected = project.id === selectedId;
            const isEditing = editingId === project.id;
            const showActions = actionsVisibleId === project.id && !isEditing;
            const projectColor = project.color || PROJECT_COLORS[0];
            const pillBg = hexToRgba(projectColor, isSelected ? 0.22 : 0.1);

            return (
              <Draggable
                key={project.id}
                draggableId={project.id}
                index={index}
                isDragDisabled={!!editingId || !onReorder}
              >
                {(dragProvided, snapshot) => (
              <div
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                {...dragProvided.dragHandleProps}
                className={`relative flex-shrink-0 group/pill ${snapshot.isDragging ? 'z-30' : ''}`}
                onMouseEnter={() => handlePillMouseEnter(project.id)}
                onMouseLeave={handlePillMouseLeave}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (wasDraggingRef.current || isEditing) return;
                    onSelect(project.id);
                  }}
                  onDoubleClick={e => startEditing(project, e)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !isEditing) onSelect(project.id);
                    if (e.key === 'F2' && !isEditing) startEditing(project, e as unknown as React.MouseEvent);
                  }}
                  className={`relative inline-flex items-center gap-1.5 pl-3 pr-3 py-1.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
                    onReorder ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                  } ${
                    isSelected
                      ? 'text-gray-900 dark:text-white font-semibold'
                      : 'text-gray-700 dark:text-gray-300'
                  } ${snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-300/50 dark:ring-indigo-700/50' : ''}`}
                  style={{
                    backgroundColor: pillBg,
                    borderColor: pillBg,
                  }}
                >
                  <div className={`inline-flex items-center gap-1.5 transition-opacity duration-150 ${
                    showActions ? 'opacity-0 invisible' : ''
                  }`}>
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: projectColor }}
                    />

                    {isEditing ? (
                      <input
                        ref={inputRef}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onBlur={() => commitRename(project)}
                        onKeyDown={e => {
                          e.stopPropagation();
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(project); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); }
                        }}
                        onClick={e => e.stopPropagation()}
                        className="bg-transparent border-none outline-none text-sm font-medium min-w-[60px] max-w-[180px] text-gray-900 dark:text-white placeholder-gray-400"
                        style={{ width: `${Math.max(editName.length, 4)}ch` }}
                      />
                    ) : (
                      <span
                        className="block max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap"
                        title={project.name}
                      >
                        {project.name}
                      </span>
                    )}

                    {project.status === 'paused' && !isEditing && (
                      <span className={`text-[9px] px-1 py-0.5 rounded flex-shrink-0 ${
                        isSelected
                          ? 'bg-amber-100/80 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      }`}>
                        Pausado
                      </span>
                    )}
                  </div>

                  {!isEditing && (
                    <div className={`absolute inset-0 flex items-center justify-center gap-1.5 transition-opacity duration-150 pointer-events-none ${
                      showActions ? 'opacity-100' : 'opacity-0'
                    }`}>
                      {onEdit && (
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            onEdit(project);
                          }}
                          className={`p-1 rounded-full ${showActions ? 'pointer-events-auto' : 'pointer-events-none'} ${
                            isSelected
                              ? 'hover:bg-black/5 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400'
                          }`}
                          title="Editar proyecto (color, columnas...)"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          onDelete(project);
                        }}
                        className={`p-1 rounded-full ${showActions ? 'pointer-events-auto' : 'pointer-events-none'} ${
                          isSelected
                            ? 'hover:bg-black/5 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300 hover:text-red-500'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-red-500'
                        }`}
                        title="Eliminar proyecto"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </div>
          )}
        </Droppable>

        {hasOverflow && (
          <>
            <div
              className={`absolute left-0 inset-y-0 w-9 z-10 pointer-events-none transition-opacity duration-200 ${
                showLeftFade ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ background: `linear-gradient(to right, ${fadeFrom}, transparent)` }}
              aria-hidden
            />
            <div
              className={`absolute right-0 inset-y-0 w-9 z-10 pointer-events-none transition-opacity duration-200 ${
                showRightFade ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ background: `linear-gradient(to left, ${fadeFrom}, transparent)` }}
              aria-hidden
            />
            <button
              type="button"
              onClick={() => scroll(-1)}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 p-1 rounded-lg text-gray-400 hover:bg-black/5 dark:hover:bg-white/10 transition-opacity ${
                showLeftFade ? 'opacity-100' : 'opacity-40 pointer-events-none'
              }`}
              aria-label="Proyectos anteriores"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 p-1 rounded-lg text-gray-400 hover:bg-black/5 dark:hover:bg-white/10 transition-opacity ${
                showRightFade ? 'opacity-100' : 'opacity-40 pointer-events-none'
              }`}
              aria-label="Proyectos siguientes"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      </DragDropContext>

      <button
        type="button"
        onClick={onCreate}
        className="flex-shrink-0 p-2 rounded-full border border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 text-gray-500 hover:text-indigo-600 transition-colors z-10"
        title="Nuevo proyecto"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ProjectSelector;
